import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { getE2EMetricsStatus, pushE2EMetrics } from '../../api/client';
import { listPipelineScenarios, activateScenario, deactivateScenario } from '../../api/scenarios';

const STATUS_COLORS = { 100: '#4caf50', 50: '#ff9800', 0: '#f44336' };
const NEUTRAL_COLOR = '#9e9e9e';

// Worst (lowest) group status in a scenario's effective metric config.
const getScenarioStatus = (scenario) => {
  const groups = scenario?.effective_metric_config?.groups;
  if (!groups) return 100;
  const statuses = Object.values(groups).map((g) => (g.status ?? 100));
  return statuses.length ? Math.min(...statuses) : 100;
};

function E2EMetricsCard({ pipeline, onToggle }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushIssues, setPushIssues] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [selectedValue, setSelectedValue] = useState('');

  const fetchStatus = async () => {
    try {
      const result = await getE2EMetricsStatus(pipeline.id);
      setStatus(result);
    } catch (error) {
      console.error('Failed to fetch E2E status:', error);
    }
  };

  const fetchScenarios = async () => {
    try {
      const result = await listPipelineScenarios(pipeline.id);
      const list = result.scenarios || [];
      setScenarios(list);
      const active = list.find((s) => s.is_active);
      setActiveScenario(active || null);
      setSelectedValue(active ? active.id : '');
    } catch (error) {
      setScenarios([]);
      setActiveScenario(null);
      setSelectedValue('');
    }
  };

  useEffect(() => {
    if (pipeline.e2e_channel_key) {
      fetchStatus();
      fetchScenarios();
    }
  }, [pipeline.id, pipeline.e2e_channel_key]);

  const handleScenarioChange = async (event) => {
    const value = event.target.value;
    if (loading) return;
    try {
      setLoading(true);
      if (value) {
        await activateScenario(pipeline.id, value);
      } else if (activeScenario) {
        await deactivateScenario(pipeline.id, activeScenario.id);
      }
      setSelectedValue(value);
      await fetchStatus();
      await fetchScenarios();
      if (onToggle) onToggle();
    } catch (error) {
      console.error('Failed to change scenario:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePushNow = async () => {
    try {
      setPushing(true);
      // A push can be accepted and still publish nothing: groups pointing at an
      // undefined path variant are dropped before the request, and the E2E API
      // rejects groups it rate-limits while still answering 200. Both come back
      // on the response, so show them rather than reporting a clean push.
      const result = await pushE2EMetrics(pipeline.id);
      setPushIssues([...(result?.warnings || []), ...(result?.api_errors || [])]);
      await fetchStatus();
      await fetchScenarios();
    } catch (error) {
      console.error('Failed to push metrics:', error);
      setPushIssues([error?.response?.data?.detail || 'Push failed']);
    } finally {
      setPushing(false);
    }
  };

  const getGroupCount = () => {
    if (!activeScenario?.effective_metric_config?.groups) return 0;
    return Object.keys(activeScenario.effective_metric_config.groups).length;
  };

  // Don't render if E2E not configured on this pipeline
  if (!pipeline.e2e_channel_key) {
    return null;
  }

  const accentColor = activeScenario ? STATUS_COLORS[getScenarioStatus(activeScenario)] : NEUTRAL_COLOR;
  const lastPushTime = status?.last_push
    ? new Date(status.last_push * 1000).toLocaleTimeString()
    : 'Never';

  return (
    <Card
      sx={{
        background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        border: `2px solid ${accentColor}40`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDark
            ? '0 4px 12px rgba(0,0,0,0.4)'
            : '0 4px 12px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TimelineIcon sx={{ color: accentColor }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
            E2E Metrics
          </Typography>
          {status?.push_enabled && (
            <Chip
              label="Auto"
              size="small"
              sx={{
                backgroundColor: `${accentColor}20`,
                color: accentColor,
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
          )}
        </Box>

        {/* Active scenario summary */}
        {activeScenario && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: isDark ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)',
              border: '1px solid',
              borderColor: isDark ? 'rgba(156, 39, 176, 0.4)' : 'rgba(156, 39, 176, 0.3)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <AccountTreeIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                Scenario Active
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {activeScenario.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {getGroupCount()} metric groups configured
            </Typography>
          </Box>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="e2e-scenario-label">Scenario</InputLabel>
          <Select
            labelId="e2e-scenario-label"
            value={selectedValue}
            onChange={handleScenarioChange}
            label="Scenario"
            disabled={loading}
            renderValue={(value) => {
              if (!value) return 'None (inactive)';
              const scenario = scenarios.find((s) => s.id === value);
              return scenario?.name || 'Scenario';
            }}
          >
            <MenuItem value="">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                None (inactive)
              </Typography>
            </MenuItem>
            {scenarios.map((scenario) => (
              <MenuItem key={scenario.id} value={scenario.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: STATUS_COLORS[getScenarioStatus(scenario)],
                    }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {scenario.name}
                      {scenario.is_active && (
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            ml: 1,
                            height: 16,
                            fontSize: '0.6rem',
                            backgroundColor: 'rgba(156, 39, 176, 0.2)',
                            color: '#9c27b0',
                          }}
                        />
                      )}
                    </Typography>
                    {scenario.description && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {scenario.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {scenarios.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            No scenarios yet. Add one under E2E Scenarios → Pipeline Scenarios.
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Last push: {lastPushTime}
            </Typography>
            {status?.push_enabled && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Interval: {status?.push_interval || 60}s
              </Typography>
            )}
          </Box>
          <Tooltip title="Push metrics now">
            <span>
              <IconButton
                size="small"
                onClick={handlePushNow}
                disabled={pushing || loading}
                sx={{
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                  },
                }}
              >
                {pushing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {pushIssues.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {pushIssues.map((issue, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{ color: 'warning.main', display: 'block' }}
              >
                {issue}
              </Typography>
            ))}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default E2EMetricsCard;
